import { LandingComponentProps, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'
import ctaVisual from '@/assets/kova/landing/usa/cta-visual.webp'
import vialGlp1 from '@/assets/kova/landing/usa/hero-vial-glp1.png'
import vials from '@/assets/kova/landing/usa/hero-vials.png'
import vialSemaglutide from '@/assets/kova/landing/usa/product-vial-semaglutide.webp'
import vialTirzepatide from '@/assets/kova/landing/usa/product-vial-tirzepatide.webp'
import stepsPackage from '@/assets/kova/landing/usa/steps-package.webp'

// US-themed creatives (DEV-35942), served on /app-usa: mobile Figma 6346:46330, desktop
// 6202:57890. LandingKovaHeroUsa brings its own header, so there is no LandingKovaHeader.
const schema: LandingScreenSchema = {
	config: { background: 'page', theme: 'usa' },

	order: [
		'LandingKovaHeroUsa',
		'LandingKovaCalculator',
		'LandingKovaResults',
		'LandingKovaSteps',
		'LandingKovaReviews',
		'LandingKovaProduct',
		'LandingKovaFaq',
		'LandingKovaCta',
		'LandingKovaFooter',
	],

	components: {
		LandingKovaHeroUsa: (): LandingComponentProps.KovaHeroUsa => ({
			ctaLabel: 'Check my eligibility',
			description: 'Lose up to 20% of body weight with doctor-prescribed medication, with free delivery to your door.',
			features: [
				'Same price, every dose',
				'No in-person clinic visit',
				'Prescription & telemed visits included',
				'No insurance required',
				'Free shipping',
			],
			headerCtaLabel: 'Check my eligibility',
			price: { label: 'Starting at', now: '$129', period: 'per month', was: '$299' },
			product: { image: vials, name: 'Semaglutide or Tirzepatide', thumb: vialGlp1 },
			promises: [
				{ icon: 'cross', label: 'No Hidden Fees' },
				{ icon: 'check', label: 'Takes ~2 minutes' },
			],
			rating: { caption: 'Over 200,000+ happy customers', label: 'Excellent!', score: '4.8' },
			title: [{ text: 'Get GLP-1 treatment ' }, { accent: true, text: 'without clinic visits or insurance' }],
			trust: [
				{ icon: 'legitscript', label: 'LegitScript certified' },
				{ icon: 'clinicians', label: 'US-licensed clinicians' },
				{ icon: 'hipaa', label: 'HIPAA aligned' },
				{ icon: 'eligibility', label: 'Eligibility before charge' },
			],
		}),

		LandingKovaCalculator: (): LandingComponentProps.KovaCalculator => ({
			calculator: {
				ctaLabel: 'Check my eligibility',
				promises: [
					{ icon: 'cross', label: 'No Hidden Fees' },
					{ icon: 'check', label: 'Cancel Anytime' },
				],
				resultLabel: 'You could easily lose:',
				title: "Let's see how much weight can you shed by this fall?",
				unit: 'lbs',
				weight: { default: 220, lossRatio: 0.2, max: 400, min: 120, step: 1 },
				weightLabel: 'Your current weight',
			},
			// The mobile frame starts the calculator right under the first screen's ticker
			// (6346:46447 sits at 1036, where the divider ends).
			flushTop: true,
			perks: ['US-Certified Pharmacy', 'Personalized Treatments', '1:1 Medical Support'],
			stats: { label: 'happy customers', value: '200,000+' },
			titleParts: [
				{ text: 'Finally Lose Weight ' },
				{ accent: true, text: 'Without the Hunger' },
				{ text: ' or Hard Dieting' },
			],
		}),

		LandingKovaResults: (): LandingComponentProps.KovaResults => ({
			caption: 'See our customers’ results:',
			customers: ['sarah', 'james', 'rachel', 'chris', 'brian'],
			heading: 'We’ll help turn down hunger signals and feel full faster.',
			text: 'GLP-1 treatment can support your weight-loss goals when traditional methods aren’t enough. With Kova’s dedicated nursing team, you’re never left guessing or going it alone.',
		}),

		LandingKovaSteps: (): LandingComponentProps.KovaSteps => ({
			ctaLabel: 'Check my eligibility',
			disclaimer: 'Illustration only. Actual vial and packaging may vary by dispensing pharmacy.',
			// package img desktop (6327:40369), exported with its "light" fades, on both
			// breakpoints: the mobile frame (6346:46571) is nearly the same crop, so mobile shows
			// the desktop shot whole, at its own 848x644 proportions. Desktop: 848x644 from the page
			// edge and 79px below the copy's top, so it runs 144px past its 584 column; the
			// caption box (6202:58120) ends with the copy column, 99px up into the shot.
			packageShot: {
				baked: true,
				desktop: { captionOverlap: 99, height: 644, overhang: 144, top: 79 },
				image: stepsPackage,
				mobileRatio: 848 / 644,
			},
			steps: [
				{ title: 'Get $360+ Off Today!' },
				{ title: 'Provider writes an Rx' },
				{ title: 'Your order ships from our licensed US pharmacies' },
				{ title: 'Get your medication' },
				{ title: 'Begin treatment' },
			],
			subtitle: 'Direct healthcare, without the long wait times or doctor denials.',
			titleParts: [{ text: 'Get GLP-1 treatment ' }, { accent: true, text: 'without a clinic visit' }],
		}),

		LandingKovaReviews: (): LandingComponentProps.KovaReviews => ({
			reviews: [
				{
					author: 'Sarah lost 90lbs with Kova',
					quote:
						'“Lost 90 pounds with Kova and the difference is huge. Less brain fog, more energy, and I finally feel like myself again!”',
				},
				{
					author: 'James lost 48lbs with Kova',
					quote:
						'“Losing the weight and seeing fuller hair has been such a confidence boost. I’m 48 pounds down with Kova and didn’t expect to look this good.”',
				},
				{
					author: 'Rachel lost 51lbs with Kova',
					quote:
						'“Ate normal, but the weight kept creeping up and I was drained by 3pm. Kova finally reset something. 51 lbs later I feel like me again.”',
				},
				{
					author: 'Chris lost 51lbs with Kova',
					quote:
						'“I wasn’t eating that badly, but the weight kept creeping up. By 3pm I was wiped out, sleeping poorly, and struggling to focus. Kova helped turn things around.”',
				},
				{
					author: 'Brian lost 42lbs with Kova',
					quote:
						'“Nothing seemed to work for me anymore. With Kova, the weight finally started coming off, and I have so much more energy throughout the day. 42 lbs down and still going.”',
				},
				{
					author: 'Lisa lost 37lbs with Kova',
					quote:
						'“After years of starting over, I finally found something that worked for me. I’m 37 lbs down with Kova, and getting dressed in the morning feels completely different now.”',
				},
			],
			title: '200k+ Happy Customers :)',
		}),

		LandingKovaProduct: (): LandingComponentProps.KovaProduct => ({
			ctaLabel: 'Check my eligibility',
			included: [
				'Medication, supplies, and instructions',
				'24/7 medical support',
				'One-on-one access to our team of clinicians',
				'Unlimited access to our Kova Med team',
				'Our promise to help you as much (or as little) as you want along the way',
				'Kova companion app included, iOS and Android',
			],
			// "+ Everything you need" is in the mobile frame (6346:46660) but not the desktop one
			// (6202:58208), where the two medications take the whole row.
			includedMobileOnly: true,
			includedTitle: '+ Everything you need',
			// Both frames stand navy vial cut-outs in the cards (6346:46614 / 6202:58208), one
			// picture per medication for both breakpoints. The frames mislabel them — mobile shows
			// the Tirzepatide vial on both cards, desktop swaps the two — so each card here gets
			// the vial its own label names.
			products: [
				{
					cadence: 'Only 1 injection per week',
					flag: { icon: 'lightning', label: 'More Affordable', tone: 'mint' },
					image: vialSemaglutide,
					name: 'Compounded Semaglutide',
					note: 'Proven, effective, more affordable.',
				},
				{
					cadence: 'Only 1 injection per week',
					flag: { icon: 'clock', label: 'Fastest Results', tone: 'purple' },
					image: vialTirzepatide,
					name: 'Compounded Tirzepatide',
					note: 'Faster results, dual-action, more expensive.',
				},
			],
			promises: [
				{ icon: 'cross', label: 'No Hidden Fees' },
				{ icon: 'check', label: 'Cancel Anytime' },
			],
			shot: 'vial',
			titleParts: [{ text: 'Medications ' }, { accent: true, text: 'In Stock ' }, { text: 'Ready to Ship' }],
		}),

		LandingKovaFaq: (): LandingComponentProps.KovaFaq => ({
			// The heading is centred on mobile as well (6346:46695).
			centeredHeading: true,
			items: [
				{
					answer: [
						"If prescribed, your medication and supplies are shipped discreetly in temperature-controlled packaging. You'll receive tracking information once your order ships, and future shipments are timed around your medication schedule.",
					],
					question: 'How does shipping work?',
				},
				{
					answer: [
						'Compounded medications are generally not covered by insurance. You may be able to use HSA or FSA funds for eligible expenses, but eligibility varies by plan. Check with your HSA or FSA provider to confirm.',
					],
					question: 'Is this covered by insurance or HSA/FSA?',
				},
				{
					answer: [
						'Yes. Your plan renews automatically at the end of each plan period, but you can cancel anytime before your next renewal through your account. See the billing details below for more information.',
					],
					question: 'Can I cancel?',
				},
				{
					answer: [
						// TODO: neutral wording, not reviewed by legal.
						'GLP-1 medications can cause side effects, including nausea and digestive changes, especially when starting or changing doses. A licensed clinician will review your health information, discuss potential risks and side effects, and determine whether compounded GLP-1 treatment is appropriate for you.',
						"Compounded medications aren’t FDA-approved and haven’t been reviewed by the FDA for safety, effectiveness, or quality. Use the Kova app to track how you're feeling and share any concerns with your clinician.",
					],
					question: 'What should I know about safety and side effects?',
				},
				{
					answer: [
						'You pay at checkout. A licensed clinician reviews every order; if treatment isn’t approved — a full refund, automatically.',
					],
					question: 'How is eligibility determined?',
				},
				{
					answer: [],
					question: 'Billing & cancellation details',
					sections: [
						{
							text: 'Your plan renews at the end of each plan period (1, 3, 6 or 12 months) at the current price for that plan.',
							title: 'Your plan renews automatically',
						},
						{
							text: 'Cancel anytime before your renewal date through your account settings or by contacting support. Cancellation stops future charges. Once your medication has shipped, the current plan period is nonrefundable.',
							title: 'How to cancel',
						},
						{
							text: "If your plan price changes, we'll email you before your next renewal so you can review the new price or cancel before you're charged.",
							title: 'If the price changes',
						},
						{
							text: 'Your plan price includes your consultation, medication, supplies, shipping, and Kova app access. There are no separate mandatory membership or dispensing fees.',
							title: 'What your plan includes',
						},
					],
				},
			],
			subtitle: 'You have questions, we have answers.',
			title: 'We’ve got you.',
		}),

		LandingKovaCta: (): LandingComponentProps.KovaCta => ({
			badge: 'Get Started',
			ctaLabel: 'Check my eligibility',
			text: 'It’s not cheating, it’s science! Lose up to 20% of your body weight (1-2lbs per week) with medically supervised safe & effective treatment from the comfort of your home.',
			titleParts: [{ text: 'Ready to Stop Food Cravings?' }, { accent: true, text: ' Save $360+ Instantly!' }],
			// visual patriotic female mob (6350:48687): the flag at 30% behind the model, on the
			// transparent ground the panel colour shows through. The desktop frame (6350:48721) is
			// the same crop 24px lower in a square, so this one serves both.
			visual: ctaVisual,
		}),

		LandingKovaFooter: (): LandingComponentProps.KovaFooter => ({
			contacts: { email: 'support@trykova.com', phone: '+1 831 480 7153' },
			// TODO: neutral wording, not reviewed by legal.
			disclaimer:
				'Compounded GLP-1 medications are not reviewed or approved by the FDA for safety, effectiveness or quality. They are prepared by a state- or FDA-registered compounding facility based on a prescription written for you by a licensed clinician. GLP-1 medications are prescription-only and appropriate only for eligible individuals. Individual results vary and are not guaranteed. This page is general information, not medical advice — talk to your clinician about whether treatment is right for you.',
			entity: [
				'LIFETECH APPS INC',
				'Registration number 10554303',
				'',
				'Registered Office:',
				'501 SILVERSIDE ROAD, SUITE 105',
				'WILMINGTON, NEW CASTLE,',
				'DELAWARE, ZIPCODE 19809',
			],
			// The current library footer (6346:46753 / 6202:58325): desktop runs in two columns.
			layout: 'columns',
			// The frame's "Shipping and Return" item has no page to link to yet.
			links: [
				{ href: 'https://trykova.com/hipaa/', label: 'Notice of Privacy Practices' },
				{
					href: 'https://trykova.com/telehealth-medical-consent/',
					label: 'Consumer Health Data Privacy Notice',
				},
				{ href: 'https://trykova.com/terms/', label: 'Notice, Terms and Conditions' },
				{ href: 'https://trykova.com/privacy/', label: 'Privacy Notice' },
			],
			madeInUsa: true,
			tagline:
				'Your daily support through GLP-1 treatment: medication, tracking, and clinician-aligned care in one place.',
		}),
	},
}

export default schema
